/**
 * Preload critical images for better performance
 * This helps load important images before they're needed
 */
class ImagePreloader {
  constructor() {
    this.cache = new Set();
    this.loading = new Map();
  }

  /**
   * Preload a single image
   * @param {string} src - Image source URL
   * @returns {Promise} - Resolves when image is loaded
   */
  preload(src) {
    if (this.cache.has(src)) {
      return Promise.resolve();
    }

    if (this.loading.has(src)) {
      return this.loading.get(src);
    }

    const promise = new Promise((resolve, reject) => {
      const img = new Image();
      
      img.onload = () => {
        this.cache.add(src);
        this.loading.delete(src);
        resolve();
      };
      
      img.onerror = () => {
        this.loading.delete(src);
        reject(new Error(`Failed to preload image: ${src}`));
      };
      
      img.src = src;
    });

    this.loading.set(src, promise);
    return promise;
  }

  /**
   * Preload multiple images
   * @param {Array<string>} sources - Array of image URLs
   * @returns {Promise} - Resolves when all images are loaded
   */
  preloadMultiple(sources) {
    return Promise.all(sources.map(src => this.preload(src)));
  }

  /**
   * Check if image is cached
   * @param {string} src - Image source URL
   * @returns {boolean}
   */
  isCached(src) {
    return this.cache.has(src);
  }

  /**
   * Clear the cache
   */
  clearCache() {
    this.cache.clear();
    this.loading.clear();
  }
}

// Create singleton instance
const imagePreloader = new ImagePreloader();

/**
 * Preload critical images on app load
 * Call this function early in your app initialization
 */
export const preloadCriticalImages = () => {
  const criticalImages = [
    '/images/me1.jpg',
    '/project/ora_app_01_hero.jpg',
  ];

  return imagePreloader.preloadMultiple(criticalImages);
};

/**
 * Preload project images
 * @param {Array} projects - Array of project objects
 */
export const preloadProjectImages = (projects) => {
  const imageSources = projects
    .flatMap(project => {
      if (project.images) {
        // Only preload first image of each project
        return [project.images[0]];
      }
      return project.src ? [project.src] : [];
    })
    .filter(Boolean);

  return imagePreloader.preloadMultiple(imageSources);
};

export default imagePreloader;
